import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FreshProductExtras, ProductDetail } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { Loading } from '../../ui/bits';
import { ProductArt, Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { Fig } from '../../ui/viz';
import { clock } from '../../lib/format.js';
import { shopKeys } from '../ShopLayout';
import { cleanSource, formatQuantity, freshProducts, parseArrivalQuantity, sortArrivals, startOfToday, stockUnitOf } from './lib/arrivals.js';
import { freshKeys } from './keys';
import { Select } from '../../ui/controls';

/** Today's arrivals: log what came in (restocks, and feeds "Arrived 5:40" / "Today's catch"). */
export function ArrivalsTab({ shopId }: { shopId: string }) {
  // Fixed for the screen's life; reopening the tab the next day picks up the new date.
  const since = useMemo(() => startOfToday(new Date()), []);
  const list = useQuery({ queryKey: freshKeys.arrivals(shopId, since), queryFn: () => api.arrivals(shopId, since), refetchInterval: 30_000 });
  const rows = useMemo(() => sortArrivals(list.data ?? []), [list.data]);

  return (
    <>
      <LogArrival shopId={shopId} since={since} />
      <section className="card dv-card dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="arr-h">
        <div className="dv-card-h">
          <h2 id="arr-h">Today's arrivals</h2>
          {rows.length ? <span className="dv-chip">{rows.length}</span> : null}
          <span className="grow" />
          <span className="dv-sub" title="Shown to customers as “Arrived 5:40”">
            Customers see the time
          </span>
        </div>
        {list.isError ? (
          <SceneError error={list.error} onRetry={() => void list.refetch()} compact />
        ) : list.isLoading ? (
          <Loading label="Loading today's arrivals" rows={2} />
        ) : rows.length ? (
          <ul aria-label="Arrivals today">
            {rows.map((a) => (
              <li key={a.id} className="fw-arrival">
                <ProductArt className="fw-arrival-art" name={a.productName} line="fresh" />
                <span className="nm">
                  <b>{a.productName}</b>
                  {a.source ? <span className="dv-sub">{a.source}</span> : null}
                </span>
                <span className="dv-sub" title="Arrived">
                  <Icon name="clock" size="sm" />
                  {clock(a.arrivedAt)}
                </span>
                <Fig text={formatQuantity(a.quantity, a.unit)} size="sm" animate={false} />
              </li>
            ))}
          </ul>
        ) : (
          <Scene scene="arrival" compact title="No catch logged yet">
            Log it as it lands. Customers see “Arrived” on those products.
          </Scene>
        )}
      </section>
    </>
  );
}

function LogArrival({ shopId, since }: { shopId: string; since: string }) {
  const id = useId();
  const qc = useQueryClient();
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('');
  const [source, setSource] = useState('');
  const [err, setErr] = useState<{ field: 'product' | 'qty' | 'source' | 'form'; text: string } | null>(null);
  const [done, setDone] = useState('');

  const stock = useQuery({ queryKey: shopKeys.stock(shopId, false), queryFn: () => api.shopStock(shopId) });
  const products = useMemo(() => freshProducts(stock.data ?? []), [stock.data]);
  // The stock unit isn't on the stock row: target-weight products count grams, others pieces.
  const detail = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api.product(productId) as Promise<ProductDetail & Partial<FreshProductExtras>>,
    enabled: !!productId,
    staleTime: 5 * 60_000,
  });
  const unit = detail.data ? stockUnitOf(detail.data) : null;

  const log = useMutation({
    mutationFn: (v: { productId: string; quantity: number; source?: string }) => api.logArrival(shopId, v.productId, v.quantity, v.source),
    onSuccess: (a) => {
      setQty('');
      setErr(null);
      setDone(`Logged ${formatQuantity(a.quantity, a.unit)} of ${a.productName} at ${clock(a.arrivedAt)}. Stock is updated.`);
      void qc.invalidateQueries({ queryKey: freshKeys.arrivals(shopId, since) });
      void qc.invalidateQueries({ queryKey: ['shop', shopId, 'stock'] });
      void qc.invalidateQueries({ queryKey: shopKeys.today(shopId) });
    },
    onError: (e) => setErr({ field: 'form', text: errorMessage(e, "Couldn't log the arrival. Try again.") }),
  });

  const submit = () => {
    setDone('');
    if (!productId) return setErr({ field: 'product', text: 'Choose the product that arrived.' });
    if (!unit) return setErr({ field: 'form', text: detail.isError ? errorMessage(detail.error) : 'Still loading the product. Try again in a moment.' });
    const q = parseArrivalQuantity(qty, unit);
    if (!q.ok) return setErr({ field: 'qty', text: q.error });
    const s = cleanSource(source);
    if (!s.ok) return setErr({ field: 'source', text: s.error });
    setErr(null);
    log.mutate({ productId, quantity: q.quantity, source: s.source });
  };

  const errFor = (f: 'product' | 'qty' | 'source') => (err && err.field === f ? err.text : null);
  return (
    <section className="card dv-card dv-rise" aria-labelledby={`${id}-h`}>
      <div className="dv-card-h">
        <h2 id={`${id}-h`}>Log an arrival</h2>
        <span className="dv-chip">Adds to stock</span>
      </div>
      {stock.isError ? (
        <SceneError error={stock.error} onRetry={() => void stock.refetch()} compact />
      ) : !stock.data ? (
        <Loading label="Loading your Fresh products" />
      ) : !products.length ? (
        <Scene scene="noProducts" compact title="No Fresh products yet">
          Fresh products appear here once they’re set up for your shop.
        </Scene>
      ) : (
        <form
          className="fw-arrive"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!log.isPending) submit();
          }}
        >
          <div className="field">
            <label htmlFor={`${id}-p`}>Product</label>
            <Select
              id={`${id}-p`}
              className="input"
              value={productId}
              aria-invalid={errFor('product') ? true : undefined}
              aria-describedby={errFor('product') ? `${id}-pe` : undefined}
              onChange={(e) => {
                setProductId(e.target.value);
                setErr(null);
              }}
            >
              <option value="">Choose a product</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>
                  {p.name}
                </option>
              ))}
            </Select>
            {errFor('product') ? (
              <span id={`${id}-pe`} className="pill st-error err-pill">
                {errFor('product')}
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor={`${id}-q`}>{unit === 'gram' ? 'Weight (g or kg)' : unit === 'piece' ? 'Pieces' : 'Quantity'}</label>
            <input
              id={`${id}-q`}
              className="input"
              inputMode={unit === 'gram' ? 'decimal' : 'numeric'}
              autoComplete="off"
              placeholder={unit === 'gram' ? '18.4 kg' : unit === 'piece' ? '40' : ''}
              value={qty}
              aria-invalid={errFor('qty') ? true : undefined}
              aria-describedby={errFor('qty') ? `${id}-qe ${id}-qh` : `${id}-qh`}
              onChange={(e) => setQty(e.target.value)}
            />
            <span id={`${id}-qh`} className="help">
              {unit === 'gram' ? 'Stock counts grams for this product.' : unit === 'piece' ? 'Stock counts pieces for this product.' : productId && detail.isLoading ? 'Checking the unit…' : 'In the product’s unit.'}
            </span>
            {errFor('qty') ? (
              <span id={`${id}-qe`} className="pill st-error err-pill">
                {errFor('qty')}
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor={`${id}-s`}>Source (optional)</label>
            <input
              id={`${id}-s`}
              className="input"
              autoComplete="off"
              placeholder="Navotas fish port"
              maxLength={120}
              value={source}
              aria-invalid={errFor('source') ? true : undefined}
              aria-describedby={errFor('source') ? `${id}-se` : undefined}
              onChange={(e) => setSource(e.target.value)}
            />
            {errFor('source') ? (
              <span id={`${id}-se`} className="pill st-error err-pill">
                {errFor('source')}
              </span>
            ) : null}
          </div>
          <div className="field">
            <span className="flabel" aria-hidden="true">
              &nbsp;
            </span>
            <button type="submit" className="btn btn-primary" disabled={log.isPending} aria-busy={log.isPending || undefined}>
              <Icon name="plus" />
              {log.isPending ? 'Saving…' : 'Log arrival'}
            </button>
          </div>
        </form>
      )}
      {err?.field === 'form' ? (
        <div className="status-msg st-error" role="alert">
          {err.text}
        </div>
      ) : null}
      <p className="sr-only" role="status" aria-live="polite">
        {done}
      </p>
      {done ? (
        <div className="status-msg st-success" aria-hidden="true">
          {done}
        </div>
      ) : null}
    </section>
  );
}
